import { CheckIcon } from '@diligentcorp/atlas-react-icons';
import { CreateIssueResponseDto } from '@issuemanager/api-client';
import { ErrorFloatingCard } from '@issuemanager/apps/client/components';
import {
  APP_ERROR_TYPE,
  useAppErrorHandler,
  useHeaderStore,
} from '@issuemanager/apps/client/contexts';
import { paths } from '@issuemanager/apps/client/paths';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { Divider, Stack, useEventCallback } from '@mui/material';
import { JSX, useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSourceParams } from '../hooks/use-source-params';
import { useCreateIssueForm } from '../hooks/useCreateIssueForm';
import { generateCallbackUrl } from './utils';

export function CreateIssue(): JSX.Element {
  const { t } = useTranslation(['common', 'issue']);
  const navigate = useNavigate();
  const { setError } = useAppErrorHandler();
  const { setHeaderProps } = useHeaderStore();

  const semantic = useAtlasSemanticThemeTokens();
  const { sourceInfo, isSourceError, isSourceExpected } = useSourceParams();

  const [isCancelling, setIsCancelling] = useState(false);

  const onCreated = useCallback(
    (issue: CreateIssueResponseDto) => {
      if (isSourceExpected && sourceInfo?.callbackUrl) {
        window.location.href = generateCallbackUrl({
          url: sourceInfo.callbackUrl,
          issueId: issue.code,
          result: 'success',
        });
      } else {
        navigate(`/${paths.issues.master}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSourceExpected, sourceInfo?.callbackUrl],
  );
  const onError = useCallback(() => {
    if (isSourceExpected && sourceInfo?.callbackUrl) {
      window.location.href = generateCallbackUrl({
        url: sourceInfo?.callbackUrl,
        result: 'error',
      });
    }
  }, [isSourceExpected, sourceInfo?.callbackUrl]);

  const {
    form,
    fields,
    issueTypeSubtypeQueryIsLoading,
    createIssueIsLoading,
    createIssueIsSuccess,
    errors,
    hasErrors,
    cleanErrors,
    onSubmit,
  } = useCreateIssueForm({
    onCreated,
    onError,
    sourceInfo: sourceInfo && {
      id: sourceInfo.sourceEntityId,
      name: sourceInfo.sourceEntityName,
      entityType: sourceInfo.sourceEntityType,
      link: sourceInfo.sourceEntityLink,
      app: sourceInfo.sourceApp,
    },
    issueType: sourceInfo?.type,
    isSourceError,
    isSourceExpected,
  });

  const handleCancel = useEventCallback(() => {
    setIsCancelling(true);
    if (isSourceExpected && sourceInfo?.callbackUrl) {
      window.location.href = generateCallbackUrl({
        url: sourceInfo.callbackUrl,
        result: 'cancellation',
      });
    } else {
      navigate(`/${paths.issues.master}`);
      setIsCancelling(false);
    }
  });

  useEffect(() => {
    if (isSourceExpected && isSourceError) {
      setError({
        type: APP_ERROR_TYPE.INVALID_SOURCE_PARAMS,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceError]);

  const disableBtns = issueTypeSubtypeQueryIsLoading || isCancelling;
  const isCreating = createIssueIsLoading || createIssueIsSuccess;

  useEffect(() => {
    setHeaderProps({
      viewTitle: {
        title: t('issue:form.new.title'),
        breadcrumbSections: !isSourceExpected
          ? [{ url: '/', title: t('common:appName') }]
          : [],
      },
      buttons: [
        {
          children: t('form.cancel'),
          onClick: handleCancel,
          disabled: disableBtns || isCreating,
          variant: 'outlined',
          'data-testid': 'cancel-button',
        },
        {
          children: isCreating
            ? t('issue:form.new.adding')
            : t('issue:form.new.add'),
          onClick: onSubmit,
          startIcon: <CheckIcon />,
          disabled: disableBtns,
          variant: 'contained',
          'data-testid': 'submit-button',
          loading: isCreating,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableBtns, onSubmit, isCreating, sourceInfo?.callbackUrl]);

  return (
    <>
      {hasErrors && <ErrorFloatingCard onClose={cleanErrors} errors={errors} />}
      <FormProvider {...form}>
        <form data-testid='add-issue'>
          <Stack px={4} py={5} direction='row' spacing={4}>
            <Stack spacing={3} flex={1}>
              {isSourceExpected && fields.source}
              {fields.title}
              {fields.description}
              {fields.issueType}
              {fields.issueSubtype}
            </Stack>
            <Divider
              orientation='vertical'
              flexItem
              style={{ color: semantic.color.background.container.value }}
            />
            <Stack flex={1} spacing={4}>
              {fields.severity}
              {fields.dueDate}
              {fields.ownerId}
              {fields.govmapGroups}
              {fields.attachments}
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
}
